import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import ProductFormModal, { ProductCategoryLabels, StockStatusLabels } from './ProductFormModal';
import {
  ShoppingBag,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  PackageCheck,
  Building,
  TrendingDown,
  Clock,
  User,
  Phone,
  Ticket
} from 'lucide-react';

const AdminStore = () => {
  const [products, setProducts] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [selectedGymId, setSelectedGymId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStockStatus, setSelectedStockStatus] = useState('');
  const [selectedIsActive, setSelectedIsActive] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 12;

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Store claims states
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'claims'
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [redemptionsTotal, setRedemptionsTotal] = useState(0);
  const [redemptionsPage, setRedemptionsPage] = useState(1);
  const [selectedRedemptionStatus, setSelectedRedemptionStatus] = useState('Pending'); // 'Pending', 'Fulfilled', 'All'
  const [searchRedemptionQuery, setSearchRedemptionQuery] = useState('');
  const [fulfillingRedemptionId, setFulfillingRedemptionId] = useState(null);

  // Fetch lightweight gyms for filters and modal dropdown
  useEffect(() => {
    const fetchGymsList = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE_URL}/api/admin/gyms/list-lightweight`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setGyms(data);
        }
      } catch (err) {
        console.error('Failed to load gyms list', err);
      }
    };
    fetchGymsList();
  }, []);

  // Fetch store products based on filters and pagination
  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');

      let url = `${API_BASE_URL}/api/admin/store/products?page=${page}&pageSize=${pageSize}`;
      if (selectedGymId) url += `&gymId=${selectedGymId}`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (selectedStockStatus) url += `&stockStatus=${selectedStockStatus}`;
      if (selectedIsActive !== '') url += `&isActive=${selectedIsActive === 'true'}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to retrieve products list.');

      const result = await response.json();
      if (result.success) {
        setProducts(result.data);
        setTotal(result.pagination.total);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedGymId, selectedCategory, selectedStockStatus, selectedIsActive]);

  // Fetch store redemptions based on filters and pagination
  const fetchRedemptions = async () => {
    setLoadingRedemptions(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API_BASE_URL}/api/admin/store/redemptions?page=${redemptionsPage}&pageSize=${pageSize}`;
      if (selectedGymId) url += `&gymId=${selectedGymId}`;
      if (selectedRedemptionStatus) url += `&status=${selectedRedemptionStatus}`;
      if (searchRedemptionQuery) url += `&searchQuery=${searchRedemptionQuery}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to retrieve redemptions list.');
      const result = await response.json();
      if (result.success) {
        setRedemptions(result.data);
        setRedemptionsTotal(result.pagination.total);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API. Please try again.');
    } finally {
      setLoadingRedemptions(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'claims') {
      fetchRedemptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, redemptionsPage, selectedGymId, selectedRedemptionStatus, searchRedemptionQuery]);

  const handleFulfillRedemption = async (id) => {
    if (fulfillingRedemptionId) return;
    setFulfillingRedemptionId(id);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/store/redemptions/${id}/fulfill`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Redemption claim fulfilled successfully!');
        setTimeout(() => setSuccess(''), 4000);
        fetchRedemptions();
      } else {
        const errText = await response.text();
        throw new Error(errText || 'Fulfillment failed.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fulfill redemption claim. Please try again.');
    } finally {
      setFulfillingRedemptionId(null);
    }
  };

  const handleCreateOrUpdate = async (formData) => {
    const token = localStorage.getItem('admin_token');
    let url = `${API_BASE_URL}/api/admin/store/products`;
    let method = 'POST';

    if (editingProduct) {
      url = `${API_BASE_URL}/api/admin/store/products/${editingProduct.id}`;
      method = 'PUT';
    }

    const response = await fetch(url, {
      method: method,
      headers: {
        'Authorization': `Bearer ${token}`
        // Do NOT set Content-Type header when sending FormData! The browser will set it with the correct boundary string.
      },
      body: formData
    });

    if (!response.ok) {
      const msg = await response.text();
      throw new Error(msg || `Failed to ${editingProduct ? 'update' : 'create'} product.`);
    }

    setSuccess(`Product successfully ${editingProduct ? 'updated' : 'created'}!`);
    setTimeout(() => setSuccess(''), 4000);
    fetchProducts();
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/store/products/${id}/activate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentActive })
      });

      if (response.ok) {
        setProducts(products.map(p => p.id === id ? { ...p, isActive: !currentActive } : p));
      } else {
        throw new Error('Failed to update product visibility.');
      }
    } catch (err) {
      console.error(err);
      setError('Error toggling product status.');
    }
  };

  const handleToggleStockStatus = async (id, currentStatus) => {
    // Normalize to numeric for the cycle logic (API still receives numeric values)
    const currentNumeric = currentStatus === 1 || currentStatus === 'InStock' ? 1
      : currentStatus === 2 || currentStatus === 'OutOfStock' ? 2
      : 3;
    let nextStatus = 1; // InStock
    if (currentNumeric === 1) nextStatus = 2; // InStock -> OutOfStock
    else if (currentNumeric === 2) nextStatus = 3; // OutOfStock -> LimitedStock
    else nextStatus = 1; // LimitedStock -> InStock

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/store/products/${id}/stock-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stockStatus: nextStatus })
      });

      if (response.ok) {
        const resData = await response.json();
        setProducts(products.map(p => p.id === id ? { ...p, stockStatus: nextStatus, stockQuantity: resData.data.stockQuantity } : p));
      } else {
        throw new Error('Failed to update product stock status.');
      }
    } catch (err) {
      console.error(err);
      setError('Error updating stock status.');
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product? It will be soft-deleted.')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/store/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Product successfully deleted.');
        setTimeout(() => setSuccess(''), 4000);
        fetchProducts();
      } else {
        throw new Error('Failed to delete product.');
      }
    } catch (err) {
      console.error(err);
      setError('Error deleting product.');
    }
  };

  const getCategoryLabel = (cat) => {
    // Handle both numeric (1) and string ("Merchandise") category values
    if (typeof cat === 'string' && ['Merchandise', 'Supplement', 'Accessory', 'Voucher', 'Other'].includes(cat)) return cat;
    return ProductCategoryLabels[cat] || cat || 'Unknown';
  };
  const getStockStatusBadge = (status) => {
    // Handle both string ("InStock") and numeric (1) values
    const normalized = status === 1 || status === 'InStock' ? 'InStock'
      : status === 2 || status === 'OutOfStock' ? 'OutOfStock'
      : status === 3 || status === 'LimitedStock' ? 'LimitedStock'
      : null;
    switch (normalized) {
      case 'InStock':
        return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">In Stock</span>;
      case 'OutOfStock':
        return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400">Out of Stock</span>;
      case 'LimitedStock':
        return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">Limited Stock</span>;
      default:
        return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-500/10 border border-gray-500/20 text-gray-400">Unknown</span>;
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1E293B] pb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-wide font-bebas flex items-center gap-2">
            <ShoppingBag className="text-[#6CD800]" /> COIN STORE PRODUCTS & CLAIMS
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Manage merchandise, vouchers, and view/fulfill customer redemptions across all gyms.
          </p>
        </div>

        {activeTab === 'catalog' && (
          <button
            onClick={() => {
              setEditingProduct(null);
              setIsModalOpen(true);
            }}
            className="bg-[#6CD800] text-[#0B0F19] text-sm font-bold px-4 py-2.5 rounded-lg flex items-center gap-1.5 hover:bg-[#5bc200] transition-colors"
          >
            <Plus size={18} /> Add New Product
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-[#0F172A] p-1.5 rounded-xl border border-[#1E293B] w-fit">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'catalog'
              ? 'bg-[#1E293B] text-yellow-400 border border-gray-800'
              : 'text-gray-400 hover:text-white'
            }`}
        >
          <ShoppingBag size={14} /> Products Catalog
        </button>
        <button
          onClick={() => setActiveTab('claims')}
          className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'claims'
              ? 'bg-[#1E293B] text-yellow-400 border border-gray-800'
              : 'text-gray-400 hover:text-white'
            }`}
        >
          <PackageCheck size={14} /> Store Claims
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="p-4 bg-[#6CD800]/10 border border-[#6CD800]/20 text-[#6CD800] rounded-lg text-sm flex items-center gap-2">
          <CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}

      {/* Filter Panel */}
      <div className="bg-[#0F172A] border border-[#1E293B] p-4 rounded-xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Gym Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
            <Building size={11} /> Gym Availability
          </label>
          <select
            value={selectedGymId}
            onChange={(e) => setSelectedGymId(e.target.value)}
            className="w-full bg-[#1E293B] text-white border-0 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
          >
            <option value="">All Gyms / Locations</option>
            {gyms.map((g) => (
              <option key={g.id} value={g.id}>{g.name} ({g.code})</option>
            ))}
          </select>
        </div>

        {activeTab === 'catalog' ? (
          <>
            {/* Category Filter */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <PackageCheck size={11} /> Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-[#1E293B] text-white border-0 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
              >
                <option value="">All Categories</option>
                {Object.entries(ProductCategoryLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* Stock Status Filter */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <TrendingDown size={11} /> Stock Status
              </label>
              <select
                value={selectedStockStatus}
                onChange={(e) => setSelectedStockStatus(e.target.value)}
                className="w-full bg-[#1E293B] text-white border-0 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
              >
                <option value="">All Stock Statuses</option>
                {Object.entries(StockStatusLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* Visibility Filter */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Eye size={11} /> Publish Status
              </label>
              <select
                value={selectedIsActive}
                onChange={(e) => setSelectedIsActive(e.target.value)}
                className="w-full bg-[#1E293B] text-white border-0 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
              >
                <option value="">All Visibility</option>
                <option value="true">Published / Active</option>
                <option value="false">Hidden / Draft</option>
              </select>
            </div>
          </>
        ) : (
          <>
            {/* Claims Status Filter */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Filter size={11} /> Claim Status
              </label>
              <select
                value={selectedRedemptionStatus}
                onChange={(e) => setSelectedRedemptionStatus(e.target.value)}
                className="w-full bg-[#1E293B] text-white border-0 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
              >
                <option value="All">All Claims</option>
                <option value="Pending">Pending Claim</option>
                <option value="Fulfilled">Fulfilled</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {/* Search Input */}
            <div className="space-y-1 sm:col-span-2 lg:col-span-2">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Search size={11} /> Search Claim
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
                <input
                  type="text"
                  value={searchRedemptionQuery}
                  onChange={(e) => setSearchRedemptionQuery(e.target.value)}
                  placeholder="Search by code, member name, phone or product..."
                  className="w-full bg-[#1E293B] text-white border-0 rounded-lg pl-9 pr-4 py-2 text-sm focus:ring-1 focus:ring-[#6CD800] outline-none"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Catalog Grid vs Claims List */}
      {activeTab === 'catalog' ? (
        loading ? (
          <div className="flex flex-col items-center justify-center h-80 space-y-4">
            <Loader2 className="animate-spin text-[#6CD800]" size={36} />
            <p className="text-gray-400 text-sm">Loading product catalog...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-16 text-center">
            <ShoppingBag className="mx-auto text-gray-600 mb-3" size={36} />
            <h3 className="text-lg font-bold text-white mb-1">No products found</h3>
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              Try adjustments to filters or add a new store product to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((p) => {
                const gym = gyms.find(g => g.id === p.gymId);
                return (
                  <div
                    key={p.id}
                    className={`bg-[#0F172A] border rounded-xl overflow-hidden shadow-md transition-all duration-300 flex flex-col justify-between ${p.isActive ? 'border-[#1E293B] hover:border-gray-700' : 'border-gray-800 opacity-60'
                      }`}
                  >
                    {/* Image Container */}
                    <div className="h-44 bg-[#0B0F19] relative flex items-center justify-center p-4 border-b border-[#1E293B]">
                      {p.imageUrl ? (
                        <img
                          src={p.imageUrl}
                          alt={p.name}
                          className="max-h-full max-w-full object-contain rounded"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-gray-600 gap-1 select-none">
                          <ShoppingBag size={28} />
                          <span className="text-[10px]">No image</span>
                        </div>
                      )}

                      {/* Stock Status Tag */}
                      <div className="absolute top-3 right-3">
                        {getStockStatusBadge(p.stockStatus)}
                      </div>

                      {/* Category Label */}
                      <div className="absolute bottom-3 left-3">
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-black/40 text-gray-300">
                          {getCategoryLabel(p.category)}
                        </span>
                      </div>
                    </div>

                    {/* Body Info */}
                    <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
                      <div>
                        <h4 className="font-bold text-white text-sm line-clamp-1">{p.name}</h4>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2 min-h-[2rem] leading-relaxed">
                          {p.description || 'No description provided.'}
                        </p>

                        {/* Gym Indicator */}
                        <div className="mt-3 flex items-center gap-1 text-[10px] text-gray-400 font-semibold bg-[#0B0F19] px-2 py-1 rounded w-fit border border-gray-800">
                          <Building size={10} className="text-[#6CD800]" />
                          <span>{gym ? gym.name : 'Global Store'}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-[#1E293B] pt-3 mt-2">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Price</span>
                          <span className="text-sm font-black text-[#6CD800] tracking-wide">
                            🪙 {p.coinCost} Coins
                          </span>
                        </div>

                        {/* Stock Info details */}
                        {p.stockQuantity !== null && (
                          <div className="text-right flex flex-col">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Qty</span>
                            <span className="text-xs font-bold text-gray-300">
                              {p.stockQuantity} Left
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="bg-[#0B0F19] px-4 py-3 border-t border-[#1E293B] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Toggle Visibility */}
                        <button
                          onClick={() => handleToggleActive(p.id, p.isActive)}
                          title={p.isActive ? 'Hide Product' : 'Publish Product'}
                          className={`p-1.5 rounded hover:bg-[#1E293B] transition-colors ${p.isActive ? 'text-emerald-400' : 'text-gray-500'
                            }`}
                        >
                          {p.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>

                        {/* Cycle stock status shortcut */}
                        <button
                          onClick={() => handleToggleStockStatus(p.id, p.stockStatus)}
                          title="Cycle Stock Status"
                          className="p-1.5 rounded hover:bg-[#1E293B] text-gray-400 hover:text-white transition-colors"
                        >
                          <PackageCheck size={16} />
                        </button>
                      </div>

                      <div className="flex items-center gap-1.5">
                        {/* Edit */}
                        <button
                          onClick={() => {
                            setEditingProduct(p);
                            setIsModalOpen(true);
                          }}
                          className="p-1.5 bg-[#1E293B] hover:bg-[#25324d] text-gray-300 hover:text-[#6CD800] rounded transition-colors"
                          title="Edit Product"
                        >
                          <Edit size={14} />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="p-1.5 bg-[#1E293B] hover:bg-red-500/10 text-gray-500 hover:text-red-400 rounded transition-colors"
                          title="Delete Product"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {total > pageSize && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs font-bold text-gray-400 bg-[#0F172A] border border-[#1E293B] rounded-lg hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-400 font-semibold px-4">
                  Page {page} of {Math.ceil(total / pageSize)}
                </span>
                <button
                  onClick={() => setPage(p => (p * pageSize < total ? p + 1 : p))}
                  disabled={page * pageSize >= total}
                  className="px-3 py-1.5 text-xs font-bold text-gray-400 bg-[#0F172A] border border-[#1E293B] rounded-lg hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )
      ) : (
        /* Claims List */
        loadingRedemptions ? (
          <div className="flex flex-col items-center justify-center h-80 space-y-4">
            <Loader2 className="animate-spin text-[#6CD800]" size={36} />
            <p className="text-gray-400 text-sm">Loading store claims...</p>
          </div>
        ) : redemptions.length === 0 ? (
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-16 text-center">
            <ShoppingBag className="mx-auto text-gray-650 mb-3" size={36} />
            <h3 className="text-lg font-bold text-white mb-1">No claims found</h3>
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              No product redemptions match the selected criteria.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {redemptions.map((r) => {
                const isPending = r.status === 'Pending';
                const isFulfilled = r.status === 'Fulfilled';
                return (
                  <div
                    key={r.id}
                    className={`p-5 rounded-xl border flex flex-col justify-between space-y-4 bg-[#0F172A] transition-all duration-300 ${isPending ? 'border-[#1E293B] hover:border-lime-500/20' : 'border-gray-900 opacity-70'
                      }`}
                  >
                    {/* Header: Product & Status */}
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#6CD800] bg-[#6CD800]/10 px-2 py-0.5 rounded border border-[#6CD800]/10">
                          {r.gymCode} • Store Claim
                        </span>
                        <h4 className="text-base font-extrabold text-white mt-1.5 leading-snug">
                          {r.productName}
                        </h4>
                        <span className="text-xs font-semibold text-gray-400 flex items-center gap-1 mt-1">
                          Cost: <span className="text-[#6CD800]">🪙 {r.coinsSpent} Coins</span>
                        </span>
                      </div>

                      <div className="text-right">
                        {isPending ? (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <Clock size={10} /> Pending Claim
                          </span>
                        ) : isFulfilled ? (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                            <CheckCircle2 size={10} /> Fulfilled
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-1">
                            <AlertTriangle size={10} /> Cancelled
                          </span>
                        )}
                        <span className="text-[9px] text-gray-500 block mt-1">
                          {new Date(r.redeemedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Member & Gym Info */}
                    <div className="grid grid-cols-2 gap-3 bg-[#0B0F19] p-3 rounded-lg border border-gray-850 text-xs text-gray-300">
                      <div className="space-y-1">
                        <span className="text-[9px] text-gray-500 uppercase font-black tracking-wide block">Member Details</span>
                        <div className="font-bold flex items-center gap-1">
                          <User size={11} className="text-gray-400 shrink-0" />
                          <span className="truncate">{r.memberName}</span>
                        </div>
                        <div className="text-gray-400 flex items-center gap-1">
                          <Phone size={11} className="text-gray-400 shrink-0" />
                          <span>{r.memberPhone}</span>
                        </div>
                      </div>

                      <div className="space-y-1 border-l border-gray-800 pl-3">
                        <span className="text-[9px] text-gray-500 uppercase font-black tracking-wide block">Gym Details</span>
                        <div className="font-bold flex items-center gap-1">
                          <Building size={11} className="text-gray-400 shrink-0" />
                          <span className="truncate">{r.gymName}</span>
                        </div>
                        <div className="text-gray-400">
                          Code: <span className="font-bold text-gray-300">{r.gymCode}</span>
                        </div>
                      </div>
                    </div>

                    {/* Code & Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-[#1E293B]">
                      <div className="flex items-center gap-1.5">
                        <Ticket size={13} className="text-gray-500" />
                        <span className="text-[10px] text-gray-400 font-bold uppercase">Code:</span>
                        <span className="text-xs font-black text-lime-400 font-mono select-all uppercase">
                          {r.redemptionCode}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(r.redemptionCode);
                            setSuccess('Redemption code copied!');
                            setTimeout(() => setSuccess(''), 2000);
                          }}
                          className="text-gray-550 hover:text-white p-1 rounded transition-colors"
                          title="Copy Code"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                          </svg>
                        </button>
                      </div>

                      {isPending ? (
                        <button
                          onClick={() => handleFulfillRedemption(r.id)}
                          disabled={fulfillingRedemptionId === r.id}
                          className="px-3 py-1.5 bg-[#6CD800] text-[#0B0F19] hover:bg-[#5bc200] disabled:opacity-50 text-[10px] font-black uppercase rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          {fulfillingRedemptionId === r.id && <Loader2 className="animate-spin" size={11} />}
                          Fulfill Claim
                        </button>
                      ) : (
                        r.fulfilledAt && (
                          <span className="text-[9px] text-gray-500 font-semibold">
                            Fulfilled on {new Date(r.fulfilledAt).toLocaleDateString()}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Redemptions Pagination */}
            {redemptionsTotal > pageSize && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <button
                  onClick={() => setRedemptionsPage(p => Math.max(1, p - 1))}
                  disabled={redemptionsPage === 1}
                  className="px-3 py-1.5 text-xs font-bold text-gray-400 bg-[#0F172A] border border-[#1E293B] rounded-lg hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-400 font-semibold px-4">
                  Page {redemptionsPage} of {Math.ceil(redemptionsTotal / pageSize)}
                </span>
                <button
                  onClick={() => setRedemptionsPage(p => (p * pageSize < redemptionsTotal ? p + 1 : p))}
                  disabled={redemptionsPage * pageSize >= redemptionsTotal}
                  className="px-3 py-1.5 text-xs font-bold text-gray-400 bg-[#0F172A] border border-[#1E293B] rounded-lg hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )
      )}

      {/* Form Modal */}
      <ProductFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleCreateOrUpdate}
        product={editingProduct}
        gyms={gyms}
      />
    </div>
  );
};

export default AdminStore;
